import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MENU_CONFIG } from '../data/menu';
import toast from 'react-hot-toast';
import { jwtDecode } from "jwt-decode";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// リトライ判定用の拡張プロパティ定義
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// リフレッシュ処理のPromiseを保持する変数
// ※複数のAPI実行を非同期で同時実行した場合にリフレッシュが複数回実行されないように制御する
let refreshPromise: Promise<string> | null = null;
let isLoggingOut = false;

export const useApiClient = () => {
  // Contextからトークンと更新用関数を取得
  const { accessToken, logout, updateAccessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const apiClient = useMemo(() => {
    // 1. axiosインスタンス生成
    const instance = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 2. リクエストインターセプター
    // アクセストークンがあればヘッダーに付与する
    instance.interceptors.request.use(
      async (config) => {
        // 型キャスト
        const customConfig = config as CustomAxiosRequestConfig;

        let pageName = "不明な画面";
        // 全カテゴリーの中から path が一致する item を探す
        for (const cat of MENU_CONFIG) {
          const foundItem = cat.items.find(item => item.path === location.pathname);
          if (foundItem) {
            pageName = foundItem.name;
            break;
          }
        }
        // 日本語を安全に送るためにエンコード
        config.headers['X-Update-Page'] = encodeURIComponent(pageName);

        // リトライ実行時（_retry = true）は、ここでヘッダーを触らない
        // (レスポンスインターセプター側で既に「最新のトークン」をセットしているため)
        if (customConfig._retry) {
          return config;
        }

        // 通常時のみ、Contextのトークンをセットする
        // if (accessToken) {
        //   config.headers.Authorization = `Bearer ${accessToken}`;
        // }
        if (accessToken) {
          try {
            // トークンをデコードして有効期限(exp)を取得
            const decoded = jwtDecode(accessToken);
            const currentTime = Date.now() / 1000;

            // 有効期限まで残り10秒を切っていたら、送信前にリフレッシュする
            if (decoded.exp && decoded.exp < currentTime + 10) {
              
              if (!refreshPromise) {
                console.log("Token is about to expire. Refreshing BEFORE sending request...");
                refreshPromise = (async () => {
                  try {
                    const refreshResponse = await axios.post(
                      `${API_BASE_URL}/auth/refresh`,
                      {},
                      { withCredentials: true }
                    );
                    
                    // バックエンドのレスポンスに合わせてキーを指定してください
                    const newAccessToken = refreshResponse.data.newAccessToken; // または accessToken
                    
                    // Contextの更新
                    updateAccessToken(newAccessToken);

                    return newAccessToken as string; 

                  } catch (e) {
                    refreshPromise = null;
                    throw e;
                  } finally {
                    setTimeout(() => { refreshPromise = null; }, 3000);
                  }
                })();
              }

              // リクエストを送信せず、ここで新しいトークンが来るのを待機！
              const newToken = await refreshPromise;
              if (newToken) {
                config.headers.Authorization = `Bearer ${newToken}`;
                return config;
              }
            } else {
              // まだ有効期限内ならそのままセット
              config.headers.Authorization = `Bearer ${accessToken}`;
            }
          } catch (e) {
            // JWTのデコードに失敗した場合はとりあえずそのまま送る
            config.headers.Authorization = `Bearer ${accessToken}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // 3. レスポンスインターセプター
    // 401エラー（期限切れ）を検知して、自動リフレッシュを行う
    instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as CustomAxiosRequestConfig;
        
        if (error.response?.status === 403) {
          toast.error("この操作を行う権限がありません。", { id: 'forbidden-error', duration: 4000 });
          // 必要に応じて navigate('/') などでトップへ戻す処理を入れてもOKです
          return Promise.reject(error);
        }

        // 401エラー かつ、まだリトライしていない場合
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          
          originalRequest._retry = true; // 無限ループ防止フラグ

          try {
            // リフレッシュ処理の共有ロジック
            // まだ誰もリフレッシュを開始していない場合のみ、リクエストを発行する
            if (!refreshPromise) {
              console.log("AccessToken expired. Starting refresh process...");
              
              refreshPromise = (async () => {
                try {
                  const refreshResponse = await axios.post(
                    `${API_BASE_URL}/auth/refresh`,
                    {},
                    { withCredentials: true }
                  );
                  const newAccessToken = refreshResponse.data.newAccessToken; // または accessToken
                  
                  // Context更新
                  updateAccessToken(newAccessToken);
                  return newAccessToken;
                } catch (e) {
                  // 失敗したらPromiseも破棄してエラーを投げる
                  refreshPromise = null;
                  throw e;
                } finally {
                  // 成功しても失敗しても、処理が終わったら変数をクリアする
                  // (ただし、後続のawaitが完了するまで少し遅延させた方が安全な場合もある)
                  refreshPromise = null;
                }
              })();
            }

            // 全員ここで待機する
            // 最初に実行した人も、後から来た人も、同じ Promise の結果を待つ
            const token = await refreshPromise;

            if (token) {
              // 新しいトークンでヘッダーを書き換えて再実行
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }

          } catch (refreshError: any) {
            console.error('Session expired. Logging out.', refreshError);
            refreshPromise = null; // 念の為クリア

            // const backendMessage = refreshError.response?.data?.message;
            
            // if (backendMessage) {
            //   // 「最大接続数に達するログインがあったため...」などがここで表示される
            //   toast.error(backendMessage, { duration: 5000 });
            // } else {
            //   // バックエンドからメッセージが無い場合の汎用メッセージ
            //   toast.error("セッションの有効期限が切れました。再度ログインしてください。", { duration: 4000 });
            // }
            
            // await logout();
            // navigate('/login');

            if (!isLoggingOut) {
              isLoggingOut = true;

              const backendMessage = refreshError.response?.data?.message;
              
              if (backendMessage) {
                // id を指定することで、万が一複数呼ばれてもトーストが1つしか出ないようにする（react-hot-toastの機能）
                toast.error(backendMessage, { id: 'session-expire', duration: 5000 });
              } else {
                toast.error("セッションの有効期限が切れました。再度ログインしてください。", { id: 'session-expire', duration: 4000 });
              }
              
              // 非同期処理でログアウトと画面遷移を行う
              logout().finally(() => {
                navigate('/login');
                // 画面遷移が終わった頃合い（例: 2秒後）にフラグを解除し、次回のログイン以降に備える
                setTimeout(() => { isLoggingOut = false; }, 2000);
              });
            }

            return Promise.reject(refreshError);
          }
        }

        // 401以外、またはリトライ済みのエラーはそのまま返す
        return Promise.reject(error);
      }
    );

    return instance;
  }, [accessToken, logout, navigate, updateAccessToken]);

  return apiClient;
};